import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { User } from 'firebase/auth';
import { getCurrentUser } from '../services/auth';
import EmailVerificationReminder from './EmailVerificationReminder';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireVerification?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireVerification = true
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    getCurrentUser().then((user) => {
      setUser(user);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    // Redirect to login but save the attempted URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Show verification reminder below the navbar */}
      {requireVerification && !user.emailVerified && (
        <Box sx={{ 
          position: 'sticky', 
          top: '64px', // Height of the navbar
          zIndex: 1000,
          px: 4, // Increased horizontal padding
          py: 2, // Increased vertical padding
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          ml: { xs: 0, sm: '256px' }, // Increased margin to account for sidebar + padding
          mr: { xs: 0, sm: 2 }, // Added right margin
          width: { 
            xs: '100%', 
            sm: 'calc(100% - 256px - 16px)' // Adjusted width calculation (sidebar + margins)
          },
          transition: 'all 0.2s ease-in-out',
          boxSizing: 'border-box' // Ensure padding is included in width calculation
        }}>
          <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
            <EmailVerificationReminder />
          </Box>
        </Box>
      )}
      {/* Always render the protected content */}
      <Box sx={{ width: '100%' }}>
        {children}
      </Box>
    </Box>
  );
};

export default ProtectedRoute; 